import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import { Subject } from 'rxjs';
import { LocalStorageService } from '../../../services/local-storage.service';
import {
  MEDIAPLAYER_DEFAULT_SPEED,
  MEDIAPLAYER_DEFAULT_VOLUME,
} from '../../../models/user-settings';

export interface AudioTrack {
  src: string;
}

// Re-exported so the components already importing them from here keep working.
// They are defined in models/user-settings, which is where the defaults are
// applied - having them here as well was a circular import.
export { MEDIAPLAYER_DEFAULT_VOLUME, MEDIAPLAYER_DEFAULT_SPEED };

@Component({
  selector: 'app-media-player',
  standalone: false,
  templateUrl: './media-player.component.html',
  styleUrl: './media-player.component.scss',
})
export class MediaPlayerComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit
{
  @Input() tracks: AudioTrack[] = [];
  @Input() minimal: boolean = false;
  @Input() theme: 'light' | 'dark' = 'light';
  @Input() disabled: boolean = false;
  @Input() applyStoredSettings: boolean = true;
  @Output() timeElapsed = new EventEmitter<number>();
  @Output() stopped = new EventEmitter<void>();
  @Output() paused = new EventEmitter<void>();
  @Output() started = new EventEmitter<void>();
  @Output() completed = new EventEmitter<void>();
  @Output() songChanged = new EventEmitter<AudioTrack>();
  @Output() audioReady = new EventEmitter<void>();
  @Output() error = new EventEmitter<any>();

  @ViewChild('audioPlayer') audioPlayer!: ElementRef<HTMLAudioElement>;
  audio!: HTMLAudioElement;
  private currentTrackIndex: number = 0;
  private destroy$ = new Subject<void>();
  private isAudioReady: boolean = false;
  isLoading: boolean = false; // Flag to manage loading state
  private loadId = 0; // Identifies the latest track load; older loads are ignored

  isPlaying: boolean = false;
  currentTime: number = 0;
  duration: number = 0;
  private retryDelay = 5000; // Delay between retries (5 seconds)
  private isOnline = true; // Track connectivity status
  private connectivityCheckInterval = 5000; // Check connectivity every 5 seconds
  private connectivityCheckTimeout: any = null; // Timeout for connectivity check

  constructor(
    private cdr: ChangeDetectorRef,
    private localStorageService: LocalStorageService,
  ) {}

  ngOnInit() {}

  isFiniteDuration(): boolean {
    return this.duration !== Number.POSITIVE_INFINITY && this.duration > 0;
  }

  ngAfterViewInit() {
    if (this.audioPlayer) {
      this.audio = this.audioPlayer.nativeElement;
      this.audio.volume = MEDIAPLAYER_DEFAULT_VOLUME;
      this.audio.playbackRate = MEDIAPLAYER_DEFAULT_SPEED;
      this.isAudioReady = true;
      this.setupAudioEvents();
      if (this.tracks.length > 0) {
        this.loadTrack(this.currentTrackIndex);
      }
      this.cdr.detectChanges();
    }

    const speedBar = document.querySelector('.speed-bar') as HTMLInputElement;
    const volumeBar = document.querySelector('.volume-bar') as HTMLInputElement;
    const speedTooltip = document.querySelector(
      '.speed-tooltip',
    ) as HTMLElement;
    const volumeTooltip = document.querySelector(
      '.volume-tooltip',
    ) as HTMLElement;

    const updateTooltipPosition = (
      slider: HTMLInputElement,
      tooltip: HTMLElement,
    ) => {
      const value = parseFloat(slider.value);
      const min = parseFloat(slider.min);
      const max = parseFloat(slider.max);
      const range = max - min;
      const percentage = (value - min) / range;
      const sliderWidth = slider.offsetWidth;
      const thumbWidth = 12; // Matches CSS width of thumb
      const offset = percentage * (sliderWidth - thumbWidth) + thumbWidth / 2;
      tooltip.style.left = `${offset}px`;
    };

    const handleMouseMove = (
      event: MouseEvent,
      slider: HTMLInputElement,
      tooltip: HTMLElement,
    ) => {
      const rect = slider.getBoundingClientRect();
      const value = parseFloat(slider.value);
      const min = parseFloat(slider.min);
      const max = parseFloat(slider.max);
      const range = max - min;
      const percentage = (value - min) / range;
      const thumbWidth = 12; // Matches CSS width of thumb
      const thumbCenter =
        rect.left + percentage * (rect.width - thumbWidth) + thumbWidth / 2;
      const mouseX = event.clientX;

      // Show tooltip if mouse is within 10px of thumb center
      const isNearThumb = Math.abs(mouseX - thumbCenter) < 10;
      if (isNearThumb) {
        slider.classList.add('thumb-hover');
      } else {
        slider.classList.remove('thumb-hover');
      }

      updateTooltipPosition(slider, tooltip);
    };

    if (speedBar && speedTooltip) {
      speedBar.addEventListener('mousemove', (event: MouseEvent) =>
        handleMouseMove(event, speedBar, speedTooltip),
      );
      speedBar.addEventListener('input', () =>
        updateTooltipPosition(speedBar, speedTooltip),
      );
      speedBar.addEventListener('mouseleave', () =>
        speedBar.classList.remove('thumb-hover'),
      );
      updateTooltipPosition(speedBar, speedTooltip); // Initial position
    }

    if (volumeBar && volumeTooltip) {
      volumeBar.addEventListener('mousemove', (event: MouseEvent) =>
        handleMouseMove(event, volumeBar, volumeTooltip),
      );
      volumeBar.addEventListener('input', () =>
        updateTooltipPosition(volumeBar, volumeTooltip),
      );
      volumeBar.addEventListener('mouseleave', () =>
        volumeBar.classList.remove('thumb-hover'),
      );
      updateTooltipPosition(volumeBar, volumeTooltip); // Initial position
    }
  }

  // Explicitly set the track list to ensuring the change detection
  setTrackList(tracks: AudioTrack[]) {
    this.tracks = [...tracks]; // Create a new array to ensure change detection
    this.currentTrackIndex = 0; // Reset to first track
    this.currentTime = 0;
    this.duration = 0;
    if (this.isAudioReady && tracks.length > 0) {
      this.loadTrack(this.currentTrackIndex);
    } else if (tracks.length > 0) {
      // Defer loading until audio is ready
      this.deferTrackLoad();
    }
    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['tracks'] && this.isAudioReady) {
      this.handleTrackChanges(changes['tracks']);
    } else if (changes['tracks'] && !this.isAudioReady) {
      this.deferTrackLoad();
    }
    if (changes['disabled'] && this.audio && this.disabled) {
      this.audio.pause(); // Pause audio when disabled
      this.isPlaying = false;
      this.cdr.detectChanges();
    }
  }

  private async checkInternetConnectivity(): Promise<boolean> {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
      const response = await fetch('https://www.google.com/favicon.ico', {
        method: 'HEAD',
        mode: 'no-cors', // Use no-cors to avoid CORS issues with favicon
        signal: controller.signal,
      });
      clearTimeout(timeoutId);
      this.isOnline = true;
      return true;
    } catch (error) {
      this.isOnline = false;
      return false;
    }
  }

  private handleTrackChanges(tracksChange: any) {
    if (!tracksChange.previousValue || tracksChange.currentValue.length === 0) {
      this.currentTrackIndex = 0;
    } else if (this.currentTrackIndex >= tracksChange.currentValue.length) {
      this.currentTrackIndex = Math.max(
        0,
        tracksChange.currentValue.length - 1,
      );
    }
    this.tracks = [...tracksChange.currentValue]; // Ensure new array for change detection
    this.loadTrack(this.currentTrackIndex);
  }

  private deferTrackLoad() {
    const checkAudio = () => {
      if (this.isAudioReady && this.tracks.length > 0) {
        this.loadTrack(this.currentTrackIndex);
        this.setupAudioEvents();
      } else {
        setTimeout(checkAudio, 100);
      }
    };
    setTimeout(checkAudio, 0);
  }

  ngOnDestroy() {
    this.loadId++; // Prevent in-flight loads from setting src / emitting audioReady
    this.destroy$.next();
    this.destroy$.complete();
    if (this.audio) {
      this.audio.pause();
      URL.revokeObjectURL(this.audio.src); // Clean up blob URL
    }
    if (this.connectivityCheckTimeout) {
      clearInterval(this.connectivityCheckTimeout);
      this.connectivityCheckTimeout = null;
    }

    // Clear any pending retry timeouts in loadTrack
    if ((this as any).retryTimeout) {
      clearTimeout((this as any).retryTimeout);
    }
  }

  private setupAudioEvents() {
    if (this.audio) {
      // Clear existing event listeners to prevent duplicates
      this.audio.onplay = null;
      this.audio.ontimeupdate = null;
      this.audio.onloadedmetadata = null;
      this.audio.onerror = null;
      this.audio.onended = null;
      this.audio.onpause = null;

      this.audio.ontimeupdate = () => {
        this.currentTime = this.audio.currentTime;
        this.timeElapsed.emit(this.currentTime);
        this.cdr.detectChanges();
      };

      this.audio.onloadedmetadata = () => {
        this.duration = this.audio.duration;
        if (this.duration === Number.POSITIVE_INFINITY) {
          this.duration = 60; // Default to 60s for streams
        }
        this.cdr.detectChanges();
      };

      this.audio.onerror = (e) => {
        this.error.emit(e);
        this.duration = 0;
        this.cdr.detectChanges();
      };

      this.audio.onended = () => {
        this.completed.emit();
        this.currentTime = 0; // Reset progress
        this.audio.currentTime = 0; // Reset audio position
        this.playNext();
      };

      this.audio.onplay = () => {
        this.isPlaying = true;
        this.started.emit();
        this.cdr.detectChanges();
      };

      this.audio.onpause = () => {
        this.isPlaying = false;
        this.paused.emit();
        this.cdr.detectChanges();
      };
    }
  }

  togglePlayPause() {
    if (this.audio && this.tracks.length > 0 && !this.isLoading) {
      if (this.isPlaying) {
        this.audio.pause();
      } else {
        this.safePlay();
      }
    }
  }

  stop() {
    if (this.audio && this.tracks.length > 0) {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.currentTime = 0;
      this.isPlaying = false;
      this.stopped.emit();
      this.cdr.detectChanges();
    }
  }

  replay() {
    if (this.audio && this.tracks.length > 0) {
      this.audio.currentTime = 0;
      this.currentTime = 0;
      this.safePlay();
    }
  }

  playPrevious() {
    if (this.audio && this.hasPrevious() && this.tracks.length > 0) {
      this.currentTrackIndex--;
      this.currentTime = 0; // Reset current time
      this.duration = 0; // Reset duration
      this.loadTrack(this.currentTrackIndex);
      this.safePlay();
      this.songChanged.emit(this.tracks[this.currentTrackIndex]);
    }
  }

  playNext() {
    if (this.audio && this.hasNext() && this.tracks.length > 0) {
      this.currentTrackIndex++;
      this.currentTime = 0; // Reset current time
      this.duration = 0; // Reset duration
      this.loadTrack(this.currentTrackIndex);
      this.safePlay();
      this.songChanged.emit(this.tracks[this.currentTrackIndex]);
    }
  }

  skipForward() {
    if (this.audio && this.tracks.length > 0) {
      const newTime = Math.min(this.audio.currentTime + 5, this.audio.duration);
      this.audio.currentTime = newTime;
      this.currentTime = newTime;
      this.cdr.detectChanges();
    }
  }

  skipBackward() {
    if (this.audio && this.tracks.length > 0) {
      const newTime = Math.max(this.audio.currentTime - 5, 0);
      this.audio.currentTime = newTime;
      this.currentTime = newTime;
      this.cdr.detectChanges();
    }
  }

  setMediaSpeed(speed: number) {
    this.audio.playbackRate = speed;
    this.cdr.detectChanges();
  }

  setMediaVolume(volume: number) {
    this.audio.volume = volume;
    this.cdr.detectChanges();
  }

  setSpeed(event: Event) {
    if (this.audio && this.tracks.length > 0) {
      const target = event.target as HTMLInputElement;
      this.audio.playbackRate = Number(target.value);
      this.localStorageService.dispatch({
        type: 'SET_MEDIA_PLAYER_PLAYBACK_SPEED',
        payload: this.audio.playbackRate,
      });
    }
  }

  setVolume(event: Event) {
    if (this.audio && this.tracks.length > 0) {
      const target = event.target as HTMLInputElement;
      const volume = Number(target.value);
      this.audio.volume = volume > 1 ? volume / 100 : volume;
      this.localStorageService.dispatch({
        type: 'SET_MEDIA_PLAYER_VOLUME',
        payload: this.audio.volume,
      });
    }
    this.cdr.detectChanges();
  }

  seek(event: Event) {
    if (this.audio && this.tracks.length > 0) {
      const target = event.target as HTMLInputElement;
      const seekTime = Number(target.value);
      this.audio.currentTime = seekTime;
      this.currentTime = seekTime;
      this.cdr.detectChanges();
    }
    this.cdr.detectChanges();
  }

  setSeekPosition(time: number) {
    if (this.audio && this.tracks.length > 0) {
      this.audio.currentTime = time;
      this.currentTime = time;
    }
  }

  getVolume(): number {
    return this.audio?.volume ?? MEDIAPLAYER_DEFAULT_VOLUME;
  }

  getPlaybackRate(): number {
    return this.audio?.playbackRate ?? MEDIAPLAYER_DEFAULT_SPEED;
  }

  addTrack(track: AudioTrack) {
    this.tracks.push(track);
  }

  removeTrack(trackId: string) {
    const index = this.tracks.findIndex((_, i) => i === this.currentTrackIndex);
    if (index !== -1) {
      this.tracks.splice(index, 1);
      if (index <= this.currentTrackIndex) {
        this.currentTrackIndex = Math.max(0, this.currentTrackIndex - 1);
        this.loadTrack(this.currentTrackIndex);
      }
    }
  }

  // Set Start Time For An Audio
  setStartTime(startTime: number) {
    if (this.audio && this.tracks.length > 0 && this.isAudioReady) {
      this.audio.currentTime = startTime;
      this.currentTime = startTime;
      this.cdr.detectChanges();
    } else {
      // Defer setting start time if audio is not ready
      setTimeout(() => {
        if (this.isAudioReady && this.tracks.length > 0) {
          this.audio.currentTime = startTime;
          this.currentTime = startTime;
          this.cdr.detectChanges();
        } else {
          this.error.emit(
            new Error(
              'Cannot set start time: Audio not ready or no tracks available',
            ),
          );
        }
      }, 100);
    }
  }

  clearTrackList() {
    this.tracks = [];
    this.currentTrackIndex = 0;
    this.currentTime = 0; // Reset current time
    this.duration = 0; // Reset duration
    if (this.audio) {
      this.audio.pause();
      this.audio.removeAttribute('src'); // Clear audio source
      this.audio.load(); // Reload to clear metadata
    }
    this.isPlaying = false;
    this.loadId++; // Invalidate any in-flight track load
    this.isLoading = false;
    this.stopped.emit();
    this.cdr.detectChanges();
  }

  private async loadTrack(index: number) {
    if (this.audio && this.tracks[index] && !this.isLoading) {
      const loadId = ++this.loadId;
      this.isLoading = true;
      this.cdr.detectChanges();

      if (this.connectivityCheckTimeout) {
        clearInterval(this.connectivityCheckTimeout);
        this.connectivityCheckTimeout = null;
      }

      this.cdr.detectChanges();

      const tryLoadTrack = async (): Promise<boolean> => {
        const track = this.tracks[index];
        try {
          this.audio.pause();
          this.audio.removeAttribute('src');
          this.audio.load();
          this.currentTime = 0;
          this.duration = 0;

          // Check connectivity before loading
          if (!(await this.checkInternetConnectivity())) {
            throw new Error('No internet connection');
          }

          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout
          const response = await fetch(track.src, {
            mode: 'cors',
            signal: controller.signal,
          });
          clearTimeout(timeoutId);
          if (!response.ok) {
            throw new Error(`Failed to fetch audio: ${response.statusText}`);
          }
          const blob = await response.blob();
          // Server allows only the '.wav' and '.mp3' files
          const isValidAudioType =
            blob.type.startsWith('audio/') ||
            (blob.type === 'application/octet-stream' &&
              track.src.toLowerCase().endsWith('.mp3')) ||
            track.src.toLowerCase().endsWith('.wav');
          if (blob.size === 0 || !isValidAudioType) {
            throw new Error(
              `Invalid audio blob: Empty or not an audio type for ${track.src}`,
            );
          }
          if (loadId !== this.loadId) {
            return true; // Superseded (page changed / cleared / destroyed)
          }
          const blobUrl = URL.createObjectURL(blob);
          this.audio.src = blobUrl;

          this.songChanged.emit(track);

          await new Promise((resolve, reject) => {
            this.audio.oncanplay = () => {
              this.duration = this.audio.duration;
              if (this.duration === Number.POSITIVE_INFINITY) {
                this.duration = 60;
              }
              resolve(null);
              this.cdr.detectChanges();
            };
            this.audio.onerror = () => {
              reject(
                new Error(`Failed to load audio metadata for ${track.src}`),
              );
            };
          });

          return true;
        } catch (error) {
          this.error.emit(error); // Emit error to parent
          return false;
        }
      };

      // Retry indefinitely until successful or no more tracks
      while (this.audio && this.tracks[index] && loadId === this.loadId) {
        if (await tryLoadTrack()) {
          if (this.connectivityCheckTimeout) {
            clearInterval(this.connectivityCheckTimeout);
            this.connectivityCheckTimeout = null;
          }
          break; // Success, exit retry loop
        }

        await new Promise((resolve) => setTimeout(resolve, this.retryDelay)); // Wait before retry
        // Check connectivity and wait until online
        if (!(await this.checkInternetConnectivity())) {
          await new Promise((resolve) => {
            this.connectivityCheckTimeout = setInterval(async () => {
              if (await this.checkInternetConnectivity()) {
                clearInterval(this.connectivityCheckTimeout);
                this.connectivityCheckTimeout = null;
                resolve(null);
              }
            }, this.connectivityCheckInterval);
          });
        }
      }

      if (loadId !== this.loadId) {
        return; // A newer load owns the player state now
      }
      this.isLoading = false;
      if (this.audio && this.tracks[index]) {
        this.audioReady.emit();
      } else {
        // If no tracks remain or audio is destroyed, emit error
        this.error.emit(
          new Error(
            `Failed to load track: ${
              this.tracks[index]?.src || 'No track available'
            }`,
          ),
        );
        this.tracks = [];
        this.duration = 0;
      }
      this.cdr.detectChanges();
    }
  }

  private async safePlay() {
    if (this.audio && this.tracks.length > 0 && !this.isLoading) {
      try {
        await this.audio.play();
      } catch (error: any) {
        this.error.emit(error);
        if (
          error.name === 'NotAllowedError' &&
          document.visibilityState !== 'visible'
        ) {
          // Suppress error logging for autoplay restrictions in inactive tab
          // Playback will be attempted when tab becomes visible
        } else {
          console.error('Playback error:', error);
        }
        this.cdr.detectChanges();
      }
    }
  }

  hasNext(): boolean {
    return this.currentTrackIndex < this.tracks.length - 1;
  }

  hasPrevious(): boolean {
    return this.currentTrackIndex > 0;
  }

  getTimeDisplay(): string {
    return `${formatTime(this.currentTime)}`; // Show only the current time
  }

  onTimeUpdate() {
    if (this.audio) {
      this.currentTime = this.audio.currentTime;
      this.timeElapsed.emit(this.currentTime);
      this.cdr.detectChanges();
    }
  }
}

function formatTime(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}
